/**
 * atomberg-wq-device-count.js
 *
 * Live count of Atomberg Intellon water monitors near a pincode. Informational
 * only — never gates serviceability.
 *
 *   POST <base>/api/v1/loading/intellon-count
 *   body    { pincode, radius_km: 5 }
 *   header  X-Request-Token: AtombergAesToken.encrypt(secret, "WATER_QUALITY|<pin>|<epoch>")
 *   200 →   { status: "success", source: "intellon", count: 408 }
 *
 * A missing, malformed, stale (>60 s) or wrong-pincode token all return 403.
 * Needs atomberg-aes-token.js and atomberg-local-cache.js loaded first.
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergWqDevicesInit) return;
  w._atombergWqDevicesInit = true;

  var CACHE_STORE = 'atomberg_wq_device_count';
  var CACHE_TTL_MS = 30 * 60 * 1000;

  /** @type {Record<string, Promise<number> | undefined>} */
  var inFlight = {};

  /**
   * Mirrors roundDeviceCount() in the water-quality repo's LoadingPage.tsx —
   * if the two drift, the PDP and /pages/water-quality disagree on the same pincode.
   * @param {number} n
   * @returns {number}
   */
  function roundCount(n) {
    if (!(n > 0)) return 0;
    var step = n < 100 ? 10 : n < 1000 ? 100 : n < 5000 ? 500 : 1000;
    return Math.floor(n / step) * step;
  }

  /**
   * Resolves 0 on any failure — never rejects, so callers can fire and forget.
   * @param {string} pincode
   * @param {string} baseUrl
   * @param {string} secretHex
   * @returns {Promise<number>}
   */
  function getCount(pincode, baseUrl, secretHex) {
    var cache = w.AtombergLocalCache;
    var aes = w.AtombergAesToken;
    if (!/^\d{6}$/.test(pincode) || !baseUrl || !secretHex || !aes) return Promise.resolve(0);

    var cached = cache ? cache.read(CACHE_STORE, pincode, CACHE_TTL_MS) : null;
    if (typeof cached === 'number') return Promise.resolve(cached);
    // Variant switches re-run the delivery check; dedupe concurrent calls per pincode
    var pending = inFlight[pincode];
    if (pending) return pending;

    var p = aes
      .encrypt(secretHex, 'WATER_QUALITY|' + pincode + '|' + Math.floor(Date.now() / 1000))
      .then(function (/** @type {string|null} */ token) {
        if (!token) return null;
        return fetch(baseUrl + '/api/v1/loading/intellon-count', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Request-Token': token },
          body: JSON.stringify({ pincode: pincode, radius_km: 5 }),
        });
      })
      .then(function (/** @type {Response|null} */ res) {
        return res && res.ok ? res.json() : null;
      })
      .then(function (/** @type {any} */ data) {
        if (!data) return 0;
        var n = Number(data.count);
        var count = isFinite(n) && n > 0 ? Math.round(n) : 0;
        // Only real answers are cached — a failed call would otherwise pin 0 for 30 minutes
        if (cache) cache.write(CACHE_STORE, pincode, count, CACHE_TTL_MS);
        return count;
      })
      .catch(function () {
        return 0;
      })
      .then(function (/** @type {number} */ count) {
        delete inFlight[pincode];
        return count;
      });

    inFlight[pincode] = p;
    return p;
  }

  w.AtombergWqDevices = { getCount: getCount, roundCount: roundCount };
})();
